import { Tab } from '@headlessui/react';
import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { SidebarFilter } from './sidebar-filter-and-categories';
import Categories from '@/components/categories/categories';
import FilterHeader from './header';
import Scrollbar from '@/components/ui/scrollbar';
import { useRouter } from 'next/router';
import { TYPES_PER_PAGE } from '@/framework/client/variables';
import { useTypes } from '@/framework/type';

interface TabsProps {
  className?: string;
  variables: any;
  showFiltersTab?: boolean;
  showCategoriesTab?: boolean;
  /** When set, restricts the categories.name facet in the Filtrar tab to these names only. */
  childCategoryNames?: string[];
}

export const MultipleFilter: React.FC<TabsProps> = ({
  className,
  variables,
  showFiltersTab = true,
  showCategoriesTab = true,
  childCategoryNames,
}) => {
  const availableTabs = useMemo(() => {
    const tabs = [];
    if (showFiltersTab) tabs.push('filters');
    if (showCategoriesTab) tabs.push('categories');
    return tabs;
  }, [showFiltersTab, showCategoriesTab]);

  const [activeTab, setActiveTab] = useState<'filters' | 'categories'>(
    (availableTabs[0] as 'filters' | 'categories') || 'filters',
  );

  const { types: groups }: any = useTypes({
    limit: TYPES_PER_PAGE,
  });

  const router = useRouter() as any;

  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  const currentPageGroupId =
    router?.components?.[router.pathname]?.props?.pageProps?.variables
      ?.group_id || null;

  const selectedMenuName = useMemo(() => {
    return groups?.find((type: any) => +type?.id === +currentPageGroupId)?.name;
  }, [groups, currentPageGroupId]);

  return (
    <div
      className={classNames(
        'flex h-full w-full flex-col rounded-xl bg-white lg:h-auto lg:border overflow-hidden',
        className,
      )}
    >
      <FilterHeader />
      <Tab.Group selectedIndex={availableTabs.indexOf(activeTab)}>
        {availableTabs.length > 1 && (
          <Tab.List className="flex w-full">
            {showFiltersTab && (
              <Tab
                className={classNames(
                  'bg-gray-100 flex-1 px-4 py-2 text-sm font-medium text-gray-700',
                  activeTab === 'filters' ? 'bg-white' : 'hover:bg-gray-100',
                )}
                onClick={() => setActiveTab('filters')}
              >
                Filtrar
              </Tab>
            )}
            {showCategoriesTab && (
              <Tab
                className={classNames(
                  'bg-gray-100 flex-1 px-4 py-2 text-sm font-medium text-gray-700',
                  activeTab === 'categories' ? 'bg-white' : 'hover:bg-gray-100',
                )}
                onClick={() => setActiveTab('categories')}
              >
                Categorias
              </Tab>
            )}
          </Tab.List>
        )}

        <Scrollbar
          className="max-h-screen w-full"
          style={{ height: 'calc(100vh - 5.35rem)' }}
        >
          <Tab.Panels>
            {showFiltersTab && (
              <Tab.Panel>
                {activeTab === 'filters' && (
                  <SidebarFilter childCategoryNames={childCategoryNames} />
                )}
              </Tab.Panel>
            )}
            {showCategoriesTab && (
              <Tab.Panel>
                {activeTab === 'categories' && (
                  <>
                    {hasMounted && selectedMenuName && (
                      <div className="border-b border-stone-200 py-3 px-4 text-gray-700 text-sm font-semibold text-center lg:hidden">
                        {selectedMenuName}
                      </div>
                    )}

                    <Categories
                      full
                      hidden={false}
                      layout="classic"
                      variables={variables}
                    />
                  </>
                )}
              </Tab.Panel>
            )}
          </Tab.Panels>
        </Scrollbar>
      </Tab.Group>
    </div>
  );
};
